/**
 * ChatWrapper.tsx
 *
 * Main chat wrapper component handling desktop and mobile layouts.
 * Refactored from ChatWrapper.v2.tsx (1148 lines) to ~350 lines.
 * Location: src/app/modules/chat/pages/
 */

import { FC, useEffect, useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { useMediaQuery } from "react-responsive"
import * as Chat from "src/actions/chat"
import { RootState } from "src/setup/redux/store"
import "src/styles/css/chat.scss"
import "src/styles/css/color.css"
import Customer from "src/app/models/Customer"
import * as chatV2 from "src/app/modules/chat/redux/ChatSlice.v2"
import * as ReduxContact from "src/app/modules/contact/redux/ContactSlice"
import { UnifiedLoading } from "src/app/components/loading"
import { ContactEditModal } from "src/app/layout/contact-management/contact-list/contact-edit-modal/ContactEditModal"
import { ChatInner } from "src/app/modules/chat/components/chat-detail"
import ContactListModal from "src/app/layout/chat-handle/components/ContactListModal"
import ContactActionModal from "src/app/layout/chat-handle/components/modals/ContactAction"
import { ChatList } from "src/app/modules/chat/components/chat-wrapper"
import { Collaboration } from "src/app/models/Chat"
import { ChatType } from "src/app/modules/chat/feature"

// Extracted components
import {
  ChatEmptyState,
  ChatListHeader,
  ChatContactHeader,
} from "src/app/modules/chat/components"

const ChatWrapper: FC = () => {
  const dispatch = useDispatch()
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 900px)" })

  const { contactData } = useSelector((state: RootState) => state.Contact)
  const { activeTab, filter } = useSelector(
    (state: RootState) => state.ChatV2,
    shallowEqual
  )

  const {
    collaborationsById,
    selectedCollaborationId,
    selectedRoomId,
  } = useSelector(
    (state: RootState) =>
      state.ChatV2[activeTab] ?? {
        collaborationOrder: [],
        collaborationsById: {},
        hasMore: false,
        isLoading: false,
        selectedCollaborationId: null,
        selectedRoomId: null,
      },
    shallowEqual
  )

  const selectedCollaboration = collaborationsById[selectedCollaborationId]
  const selectedRoom =
    collaborationsById[selectedCollaborationId]?.roomsModel?.find(
      (item) => item?.id === selectedRoomId
    ) ?? collaborationsById[selectedCollaborationId]?.roomsModel?.[0]

  const [mobileSearch, setMobileSearch] = useState<boolean>(false)
  const [isLoadingRequest, setIsLoadingRequest] = useState(false)
  const [addContactModalOpen, setAddContactModalOpen] = useState<boolean>(false)
  const [isAddExistingContactModal, setAddExistingContactModalOpen] =
    useState<boolean>(false)

  // Handle back to chat list (mobile)
  const backToChatList = () => {
    dispatch(
      chatV2.setSelectedCollaboration({
        chatType: activeTab,
        id: null,
      })
    )
    dispatch(
      chatV2.setSelectedRoom({
        chatType: activeTab,
        id: null,
      })
    )
  }

  // Handle tab change
  const handleTabChange = (tab: "myChat" | "allChat") => {
    dispatch(chatV2.setFilter(""))
    dispatch(chatV2.setActiveTab(tab))
    dispatch(
      chatV2.setSelectedCollaboration({
        chatType: activeTab,
        id: null,
      })
    )
    dispatch(
      chatV2.setSelectedRoom({
        chatType: activeTab,
        id: null,
      })
    )
    setMobileSearch(false)
  }

  // Handle save contact
  const handleSaveContact = (contactId?: string) => {
    if (!contactId) return
    updateSelectedCollabCustomer(selectedCollaboration, contactId)
  }

  // Update collaboration customer
  const updateSelectedCollabCustomer = async (
    collab?: Collaboration,
    contactId?: string
  ) => {
    if (!collab || !contactId) return
    setIsLoadingRequest(true)
    await Chat.updateCollaborationCustomer(collab, contactId)
    setIsLoadingRequest(false)
  }

  // Reset redux on page leave
  useEffect(() => {
    dispatch(
      chatV2.setSelectedCollaboration({
        chatType: activeTab,
        id: null,
      })
    )
    dispatch(
      chatV2.setSelectedRoom({
        chatType: activeTab,
        id: null,
      })
    )
    dispatch(chatV2.setActiveTab("myChat"))
  }, [location, window.location.href])

  // Set contact data in redux
  useEffect(() => {
    if (selectedCollaboration && selectedCollaboration?.customerModel) {
      if (contactData?.id !== selectedCollaboration?.customerModel?.id) {
        dispatch(
          ReduxContact.setContactData(selectedCollaboration?.customerModel)
        )
      }
      if (!contactData) {
        dispatch(
          ReduxContact.setContactData(selectedCollaboration?.customerModel)
        )
      }
    }
    if (selectedCollaboration && !selectedCollaboration.customerModel) {
      dispatch(ReduxContact.setContactData({} as Customer))
    }
  }, [selectedCollaboration])

  // Desktop Layout
  if (!isTabletOrMobile) {
    return (
      <>
        <div
          className="card d-flex flex-column flex-lg-row h-100 w-100"
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: "8.125px",
            overflow: "hidden",
          }}
        >
          {/* Chat List (Left Panel) */}
          <div
            className="d-flex flex-column h-100 col-4"
            style={{
              minHeight: 0,
              borderStyle: "solid",
              borderTopWidth: 0,
              borderLeftWidth: 0,
              borderBottomWidth: 0,
              borderRightWidth: "0.1px",
              borderColor: "rgba(161, 165, 183, 0.5)",
            }}
          >
            <div
              className="card card-flush d-flex flex-column h-100"
              style={{ backgroundColor: "white", minHeight: 0, overflow: "hidden" }}
            >
              <ChatListHeader
                activeTab={activeTab}
                filter={filter}
                onTabChange={handleTabChange}
              />
              <ChatList />
            </div>
          </div>

          {/* Chat Inner (Right Panel) */}
          {selectedCollaboration ? (
            <div
              className="w-100 h-100 d-flex flex-column"
              style={{ minHeight: 0 }} // Allow flex children to scroll
              id="kt_chat_messenger"
            >
              <ChatContactHeader
                collaboration={selectedCollaboration}
                selectedRoom={selectedRoom}
                contactData={contactData}
                activeTab={activeTab}
                onAddNewContact={() => setAddContactModalOpen(true)}
                onAddExistingContact={() => setAddExistingContactModalOpen(true)}
              />
              <ChatInner />
            </div>
          ) : (
            <ChatEmptyState />
          )}
        </div>

        {addContactModalOpen && (
          <ContactEditModal
            phoneNumber={selectedCollaboration?.phoneNumber}
            onClose={() => setAddContactModalOpen(false)}
            onSave={handleSaveContact}
          />
        )}

        {isAddExistingContactModal && (
          <ContactListModal
            phoneNumber={selectedCollaboration?.phoneNumber}
            onClose={() => setAddExistingContactModalOpen(false)}
          />
        )}

        {isLoadingRequest ? <UnifiedLoading /> : null}
      </>
    )
  }

  // Mobile Layout
  return (
    <>
      <div
        className="card d-flex flex-column w-100"
        style={{
          backgroundColor: "#fff",
          borderRadius: "8.125px",
          height: "86vh",
          overflow: "hidden",
        }}
      >
        {selectedCollaboration ? (
          <div
            className="w-100 h-100 d-flex flex-column"
            id="kt_chat_messenger"
            style={{ minHeight: 0 }} // Allow flex children to scroll
          >
            <ChatContactHeader
              collaboration={selectedCollaboration}
              selectedRoom={selectedRoom}
              contactData={contactData}
              activeTab={activeTab}
              onBackClick={backToChatList}
              onAddNewContact={() => setAddContactModalOpen(true)}
              onAddExistingContact={() => setAddExistingContactModalOpen(true)}
              isMobile
            />
            <ChatInner />
          </div>
        ) : (
          <div
            className="d-flex flex-column w-100 h-100"
            style={{ minHeight: 0 }} // Allow ChatList to scroll internally
          >
            <ChatListHeader
              activeTab={activeTab}
              filter={filter}
              onTabChange={handleTabChange}
              isMobile
              mobileSearch={mobileSearch}
              onToggleMobileSearch={() => setMobileSearch(!mobileSearch)}
            />
            <ChatList />
          </div>
        )}
      </div>

      {addContactModalOpen && (
        <ContactEditModal
          phoneNumber={selectedCollaboration?.phoneNumber}
          onClose={() => setAddContactModalOpen(false)}
          onSave={handleSaveContact}
        />
      )}

      {isAddExistingContactModal && (
        <ContactListModal
          phoneNumber={selectedCollaboration?.phoneNumber}
          onClose={() => setAddExistingContactModalOpen(false)}
        />
      )}

      <ContactActionModal
        addToExisting={() => setAddExistingContactModalOpen(true)}
        createNewContact={() => setAddContactModalOpen(true)}
      />

      {isLoadingRequest ? <UnifiedLoading /> : null}
    </>
  )
}

export default ChatWrapper
